/**
 * @file PrivateChatSection.js
 * @author Simon Tenedero
 * @created 2025-05-26
 * @lastModified 2025-05-28
 * @description file containing PrivateChatSection component
 */

import PrivateChatTabs from "./PrivateChatTabs";
import PrivateChatList from "./PrivateChatList";
import InvitationList from "./InvitationList";
import CreatePrivateChatButton from "./CreatePrivateChatButton";
import {
  handleAcceptInvitation,
  handleRejectInvitation,
} from "../../utils/privateChatUtils";
import { useUser } from "../../context/UserContext";
import { useState, useEffect } from "react";

/**
 * This component manages the panes related to privateChat - PrivateChatTabs, InvitationList, PrivateChatList - (excluding the actual private chat itself).
 * includes the list of chats the user is a member of, as well as pending invitations
 *
 * @param {Object} props - The component properties
 * @param {Array} props.privateChats - Array of private chat objects.
 * @param {Function} props.setSelectedPrivateChat - Callback function to handle chat selection.
 * @param {Map} props.privateChatMembers - Map where key-val pairs are privateChatId and its members.
 * @param {Function} props.setSelectedPrivateChatMembers - Function to set the selected private chat members.
 * @param {Array} props.pendingPrivateChatMembers - Array of pending private chat members.
 * @param {Function} props.setSelectedPrivateChatPendingMembers - Function to set the selected pending private chat members.
 * @param {Array} props.privateTabs - Array of objects representing private chat tabs, each with an id and name.
 * @param {string} props.selectedPrivateTab - The currently selected private tab's id.
 * @param {Function} props.setSelectedPrivateTab - Callback function to handle private tab selection.
 * @param {Array} props.invitations - Array of invitation objects, used to display a notification dot if there are pending invitations.
 * @param {Function} props.setNotification - Function to set notification messages.
 *
 * @returns {JSX.Element} The rendered PrivateChatSection component
 */
const PrivateChatSection = ({
  privateChats,
  setSelectedPrivateChat,
  privateChatMembers,
  setSelectedPrivateChatMembers,
  pendingPrivateChatMembers,
  setSelectedPrivateChatPendingMembers,
  privateTabs,
  selectedPrivateTab,
  setSelectedPrivateTab,
  invitations,
  setNotification,
}) => {
  const { user } = useUser();

  const [pendingChatId, setPendingChatId] = useState(null);

  /**
   * Handles accepting a chat invitation and updates the UI
   * Trying to make it automatically open up invitation, but its not working.
   */
  const handleAcceptInvite = async (invitationId, chatId) => {
    try {
      console.log("Accepting invitation:", { invitationId, chatId });

      await handleAcceptInvitation(invitationId, chatId, user);
      setPendingChatId(chatId); // Set the chat we're waiting for

      /**
       * We cannot directly open the new chat here, because of closure - when handleAcceptInvite() runs, it has the current copy of privateChats,
       * and by the time the chat is created, this copy may be outdated, i.e. it becomes "stale"
       */

      // Show success notification
      setNotification("Invitation accepted.");
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error accepting invitation:", error);
      setNotification("Failed to accept invitation. Please try again.");
      setTimeout(() => setNotification(""), 3000);
    }
  };

  // Watch for the chat we just accepted to appear, so we can open it.
  useEffect(() => {
    if (pendingChatId && privateChats.length > 0) {
      const foundChat = privateChats.find((chat) => chat.id === pendingChatId);
      if (foundChat) {
        setSelectedPrivateChat(foundChat);
        setSelectedPrivateChatMembers(
          privateChatMembers.get(pendingChatId) || [],
        );
        setSelectedPrivateChatPendingMembers(
          pendingPrivateChatMembers.get(pendingChatId) || [],
        );
        setPendingChatId(null); // Clear pending
        setSelectedPrivateTab("messagesTab"); // Switch to messages tab
      }
    }
  }, [
    privateChats,
    pendingChatId,
    privateChatMembers,
    pendingPrivateChatMembers,
  ]);

  /**
   * Handles rejecting a chat invitation and updates the UI
   */
  const handleRejectInvite = async (invitationId, chatId) => {
    try {
      console.log("Rejecting invitation:", { invitationId });

      await handleRejectInvitation(invitationId, chatId, user);

      // Show notification
      setNotification("Invitation declined.");
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error rejecting invitation:", error);
      setNotification("Failed to decline invitation. Please try again.");
      setTimeout(() => setNotification(""), 3000);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Tabs for Messages and Invitations */}
      <PrivateChatTabs
        privateTabs={privateTabs}
        selectedPrivateTab={selectedPrivateTab}
        onSelectPrivateTab={setSelectedPrivateTab}
        invitations={invitations}
      />

      {/* Content Area - switches between Messages and Invitations */}
      <div className="flex-1 w-full overflow-hidden">
        {selectedPrivateTab === "messagesTab" ? (
          /* Messages Tab Content */
          <div className="h-full overflow-y-scroll">
            <PrivateChatList
              privateChats={privateChats}
              setSelectedPrivateChat={setSelectedPrivateChat}
              privateChatMembers={privateChatMembers}
              setSelectedPrivateChatMembers={setSelectedPrivateChatMembers}
              pendingPrivateChatMembers={pendingPrivateChatMembers}
              setSelectedPrivateChatPendingMembers={
                setSelectedPrivateChatPendingMembers
              }
            />
          </div>
        ) : (
          /* Invitations Tab Content */
          <div className="h-full">
            <InvitationList
              invitations={invitations}
              onAcceptInvite={handleAcceptInvite}
              onRejectInvite={handleRejectInvite}
            />
          </div>
        )}
      </div>

      {/* Create Chat Button - always at bottom */}
      <div className="flex-shrink-0 border-t border-base-100">
        <CreatePrivateChatButton
          privateChats={privateChats}
          setSelectedPrivateChat={setSelectedPrivateChat}
          setSelectedPrivateTab={setSelectedPrivateTab}
          setSelectedPrivateChatMembers={setSelectedPrivateChatMembers}
          privateChatMembers={privateChatMembers}
          setSelectedPrivateChatPendingMembers={
            setSelectedPrivateChatPendingMembers
          }
          pendingPrivateChatMembers={pendingPrivateChatMembers}
        />
      </div>
    </div>
  );
};

export default PrivateChatSection;
